import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useTable } from '../context/TableContext';
import { useTerminal } from '../context/TerminalContext';
import { useWaiterAuth } from '../context/WaiterAuthContext';
import { useOutlet } from '../../context/OutletContext';
import WaiterSidebar from '../components/WaiterSidebar';
import './WaiterDashboard.css';

const WaiterDashboard = () => {
  const navigate = useNavigate();
  const { tables, orders: tableOrders } = useTable();
  const { currentTerminal, terminalOrders } = useTerminal();
  const { currentWaiter } = useWaiterAuth();
  const { selectedOutlet } = useOutlet();

  const [stats, setStats] = useState({
    todaySales: 0,
    todayOrders: 0,
    activeOrders: 0,
    completedToday: 0,
    avgOrderValue: 0,
    topItems: []
  });

  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load dashboard stats
  useEffect(() => {
    const loadStats = async () => {
      if (!selectedOutlet) return;

      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Get today's orders
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('outletId', '==', selectedOutlet.id),
          where('createdAt', '>=', today),
          orderBy('createdAt', 'desc'),
          limit(100)
        );

        const snapshot = await getDocs(q);
        const ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Calculate stats
        const completedOrders = ordersData.filter(o => o.status === 'completed');
        const activeOrders = ordersData.filter(o => ['pending', 'in_progress', 'prepared'].includes(o.status));

        const totalSales = completedOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        const avgValue = completedOrders.length > 0 ? totalSales / completedOrders.length : 0;

        // Get top items
        const itemCounts = {};
        ordersData.forEach(order => {
          order.items?.forEach(item => {
            if (itemCounts[item.name]) {
              itemCounts[item.name]++;
            } else {
              itemCounts[item.name] = 1;
            }
          });
        });

        const topItems = Object.entries(itemCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        setStats({
          todaySales: totalSales,
          todayOrders: ordersData.length,
          activeOrders: activeOrders.length,
          completedToday: completedOrders.length,
          avgOrderValue: avgValue,
          topItems
        });

        setRecentOrders(ordersData.slice(0, 10));
        setLoading(false);
      } catch (error) {
        console.error('Error loading stats:', error);
        setLoading(false);
      }
    };

    loadStats();
  }, [selectedOutlet]);

  const getTableStatus = (table) => {
    const tableOrdersList = tableOrders.filter(o => o.tableId === table.id && o.status !== 'completed');
    if (tableOrdersList.length > 0) return 'occupied';
    return table.status || 'available';
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#f59e0b',
      'in_progress': '#3b82f6',
      'prepared': '#8b5cf6',
      'completed': '#10b981',
      'cancelled': '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="waiter-dashboard-container">
      <WaiterSidebar currentWaiter={currentWaiter} />

      <div className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>📊 Dashboard</h1>
            <p>Welcome back, {currentWaiter?.name || 'Waiter'}!</p>
          </div>
          {currentTerminal && (
            <div className="current-terminal-badge">
              🖥️ {currentTerminal.name} ({currentTerminal.terminalNumber})
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card revenue">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <div className="stat-value">₹{stats.todaySales.toFixed(2)}</div>
              <div className="stat-label">Today's Sales</div>
            </div>
          </div>

          <div className="stat-card orders">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <div className="stat-value">{stats.todayOrders}</div>
              <div className="stat-label">Total Orders</div>
            </div>
          </div>

          <div className="stat-card active">
            <div className="stat-icon">🔥</div>
            <div className="stat-content">
              <div className="stat-value">{stats.activeOrders}</div>
              <div className="stat-label">Active Orders</div>
            </div>
          </div>

          <div className="stat-card completed">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{stats.completedToday}</div>
              <div className="stat-label">Completed</div>
            </div>
          </div>

          <div className="stat-card avg">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <div className="stat-value">₹{stats.avgOrderValue.toFixed(0)}</div>
              <div className="stat-label">Avg Order Value</div>
            </div>
          </div>

          <div className="stat-card tables">
            <div className="stat-icon">🪑</div>
            <div className="stat-content">
              <div className="stat-value">
                {tables.filter(t => getTableStatus(t) === 'occupied').length}/{tables.length}
              </div>
              <div className="stat-label">Tables Occupied</div>
            </div>
          </div>
        </div>

        <div className="dashboard-content-grid">
          {/* Recent Orders */}
          <div className="dashboard-section recent-orders-section">
            <div className="section-header">
              <h2>📦 Recent Orders</h2>
              <button className="btn-view-all" onClick={() => navigate('/waiter/order-line')}>
                View All
              </button>
            </div>

            <div className="orders-list">
              {recentOrders.slice(0, 5).map(order => (
                <div key={order.id} className="order-item">
                  <div className="order-info">
                    <div className="order-number">#{order.orderNumber}</div>
                    <div className="order-details">
                      <span className="table-info">Table {order.tableNumber}</span>
                      <span className="order-time">
                        {order.timestamp?.toDate ? new Date(order.timestamp.toDate()).toLocaleTimeString() : 'Now'}
                      </span>
                    </div>
                  </div>
                  <div className="order-meta">
                    <div
                      className="order-status-badge"
                      style={{ background: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </div>
                    <div className="order-total">₹{order.total?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Items */}
          <div className="dashboard-section top-items-section">
            <div className="section-header">
              <h2>🏆 Top Items Today</h2>
            </div>

            <div className="top-items-list">
              {stats.topItems.map((item, idx) => (
                <div key={idx} className="top-item">
                  <div className="item-rank">#{idx + 1}</div>
                  <div className="item-name">{item.name}</div>
                  <div className="item-count">{item.count} orders</div>
                </div>
              ))}

              {stats.topItems.length === 0 && (
                <div className="no-data">No orders yet today</div>
              )}
            </div>
          </div>
        </div>

        {/* Tables Overview */}
        <div className="dashboard-section tables-section">
          <div className="section-header">
            <h2>🪑 Tables Overview</h2>
            <button className="btn-view-all" onClick={() => navigate('/waiter/tables')}>
              Manage Tables
            </button>
          </div>

          <div className="tables-grid">
            {tables.slice(0, 12).map(table => {
              const status = getTableStatus(table);
              return (
                <div
                  key={table.id}
                  className={`table-card ${status}`}
                  onClick={() => navigate(`/waiter/take-order/${table.id}`)}
                >
                  <div className="table-number">Table {table.tableNumber}</div>
                  <div className="table-capacity">👥 {table.capacity} seats</div>
                  <div className={`table-status-badge ${status}`}>
                    {status === 'occupied' ? '🔴 Occupied' : '🟢 Available'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <button className="action-btn" onClick={() => navigate('/waiter/order-line')}>
            📋 Order Line
          </button>
          <button className="action-btn" onClick={() => navigate('/waiter/tables')}>
            🪑 Table Management
          </button>
          <button className="action-btn" onClick={() => navigate('/waiter/report')}>
            📊 Day Report
          </button>
          <button className="action-btn" onClick={() => navigate('/waiter/terminals')}>
            🖥️ Terminals
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaiterDashboard;
